import { useEffect } from 'react';
import Header from '../../../components/base/Header';
import Footer from '../../../components/base/Footer';
import { Link } from 'react-router-dom';

export default function AirCommunicationBlog() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <Header />

      <section className="relative py-16 bg-gradient-to-r from-emerald-500 to-teal-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-6">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <i className="ri-windy-line text-5xl text-white"></i>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              空気中の会話
            </h1>
            <p className="text-xl text-white/90 mb-6">
              ── 揮発性有機化合物（VOCs）という沈黙の言語 ──
            </p>
            <div className="flex items-center justify-center gap-4 text-white/90">
              <span className="flex items-center">
                <i className="ri-calendar-line mr-2"></i>
                2026年12月22日
              </span>
              <span className="flex items-center">
                <i className="ri-user-line mr-2"></i>
                ももちゃん
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/0aedfeee342a6daaa1b95e3e3a672832.jpg"
                alt="空気中の会話"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <article className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="mb-12">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                植物は、匂いを使って会話します。
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                私たち人間にとって「匂い」とは、好き嫌いを分けたり、過去の記憶を呼び起こしたりする、どちらかといえば感覚的な存在です。
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                しかし植物にとって、匂いはまったく違う意味を持ちます。
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                それは、状況を伝え、危険を知らせ、仲間を守るための情報。
              </p>
              <p className="text-lg text-gray-700 leading-relaxed font-medium text-emerald-800">
                つまり匂いは、植物にとっての言語なのです。
              </p>
            </div>

            <div className="mb-12 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-emerald-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">🌿</span>
                植物が放つ"言葉"の正体
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  植物が空気中に放出する匂いの正体は、<br />
                  <strong className="text-emerald-700">揮発性有機化合物（VOCs：Volatile Organic Compounds）</strong><br />
                  と呼ばれる化学物質です。
                </p>
                <p className="mb-4">代表的なものには、次のような物質があります。</p>
                
                <div className="space-y-4">
                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <h3 className="text-xl font-bold text-emerald-700 mb-2">メチルジャスモネート</h3>
                    <p>防御反応のスイッチとして働く信号分子</p>
                  </div>

                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <h3 className="text-xl font-bold text-emerald-700 mb-2">グリーンリーフボラタイル（GLVs）</h3>
                    <p>葉が傷ついた瞬間に放出される「緊急信号」</p>
                  </div>

                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <h3 className="text-xl font-bold text-emerald-700 mb-2">テルペン類</h3>
                    <p>昆虫や周囲の生物の行動に影響を与える多様な化合物群</p>
                  </div>
                </div>

                <p className="mt-6">
                  これらは、「いい香り」「青臭い匂い」として感じられることもありますが、植物にとっては感情的な表現ではありません。
                </p>
                <p className="font-medium text-emerald-800">
                  何が起きているのかを正確に伝えるためのメッセージです。
                </p>
              </div>
            </div>

            <div className="mb-12 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-teal-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">🔬</span>
                匂いが「会話」だと証明された実験
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  では、本当にこの匂いは情報として機能しているのでしょうか。
                </p>
                <p>
                  それを検証するため、多くの研究で次のような実験が行われてきました。
                </p>

                <div className="bg-white rounded-2xl p-6 my-6 border-l-4 border-teal-400">
                  <h3 className="text-xl font-bold text-teal-700 mb-4">典型的な実験デザイン</h3>
                  <ul className="space-y-2 list-disc list-inside">
                    <li>昆虫の食害を受けた植物</li>
                    <li>食害を受けていない植物</li>
                  </ul>
                  <p className="mt-4">
                    この2群を用意し、風向きを厳密に制御した環境で配置します。
                  </p>
                  <p className="mt-2 font-medium">
                    ポイントは、植物同士が直接触れないこと。
                  </p>
                  <p className="mt-2">
                    そのうえで、食害を受けていない植物の中で防御関連遺伝子の発現量を測定します。
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-12 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-cyan-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">📊</span>
                結果｜「何も起きていないのに、防御が始まる」
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>結果は非常に明確でした。</p>

                <div className="bg-white rounded-2xl p-6 my-6">
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <span className="text-cyan-600 mr-2">✓</span>
                      <span>匂いを受信した植物で防御関連遺伝子の発現が有意に上昇</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-600 mr-2">✓</span>
                      <span>実際に食害される前から防御の準備が始まっていた</span>
                    </li>
                  </ul>
                </div>

                <p>
                  つまり植物は、「近くで何かが起きている」という情報を、空気を通して受け取っていたのです。
                </p>

                <p>これは偶然でも、単なる刺激反応でもありません。</p>

                <div className="bg-cyan-100 rounded-2xl p-6 my-6 text-center">
                  <p className="text-xl font-bold text-cyan-800">
                    明確な<br />
                    情報の受信 → 解釈 → 応答<br />
                    というプロセスが存在しています。
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-12 bg-gradient-to-br from-emerald-50 to-green-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-emerald-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">🌐</span>
                空気を介した"沈黙の警報網"
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>この仕組みは、動けない植物にとって極めて合理的です。</p>

                <div className="bg-white rounded-2xl p-6 my-6">
                  <ol className="space-y-3 list-decimal list-inside">
                    <li>被害を受けた植物は、匂いを放つ</li>
                    <li>周囲の植物は、その匂いを受け取る</li>
                    <li>被害が及ぶ前に、防御を整える</li>
                  </ol>
                </div>

                <p>
                  植物たちは、空気中に見えない警報網を張り巡らせています。
                </p>

                <p>
                  誰かが叫ぶわけでも、音が鳴るわけでもありません。
                </p>

                <p className="font-medium text-emerald-800">
                  それでも確かに、危険は共有されている。
                </p>
              </div>
            </div>

            <div className="mb-12 bg-gradient-to-br from-teal-50 to-emerald-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-teal-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">💭</span>
                匂いは「感情」ではなく「戦略」
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  ここで重要なのは、植物が匂いを放つのは感情表現ではない、という点です。
                </p>

                <p>恐怖や怒りではなく、生き延びるための戦略。</p>

                <div className="bg-white rounded-2xl p-8 my-6 text-center border-2 border-teal-200">
                  <p className="text-2xl font-bold text-teal-800">
                    植物の匂いは、<br />
                    沈黙の中で交わされる<br />
                    高度に洗練された情報言語なのです。
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-12">
              <span className="px-4 py-2 bg-emerald-100 text-emerald-700 text-sm rounded-full">
                #植物科学
              </span>
              <span className="px-4 py-2 bg-emerald-100 text-emerald-700 text-sm rounded-full">
                #VOCs
              </span>
              <span className="px-4 py-2 bg-emerald-100 text-emerald-700 text-sm rounded-full">
                #植物コミュニケーション
              </span>
              <span className="px-4 py-2 bg-emerald-100 text-emerald-700 text-sm rounded-full">
                #揮発性有機化合物
              </span>
            </div>

            <div className="text-center">
              <Link
                to="/essay"
                className="inline-flex items-center bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                ブログ一覧に戻る
              </Link>
            </div>
          </div>
        </div>
      </article>

      <section className="py-16 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-emerald-800 mb-8 text-center">
              関連記事
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Link to="/blog/plant-communication" className="block group">
                <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 hover:border-emerald-200">
                  <h3 className="text-lg font-bold text-gray-800 group-hover:text-emerald-600 transition-colors mb-2">
                    植物は本当に「会話」しているのか？
                  </h3>
                  <p className="text-gray-600 text-sm">沈黙の中で交わされる、生物学的コミュニケーション</p>
                </div>
              </Link>
              <Link to="/blog/underground-network" className="block group">
                <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 hover:border-emerald-200">
                  <h3 className="text-lg font-bold text-gray-800 group-hover:text-emerald-600 transition-colors mb-2">
                    地下の会話：菌根菌ネットワーク
                  </h3>
                  <p className="text-gray-600 text-sm">見えない場所で、森はつながっている</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}